import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Camera,
  CheckCircle2,
  Crop,
  FolderOpen,
  Image as ImageIcon,
  Lock,
  RotateCw,
  ScanLine,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { VaultCategoryDto, VaultDocumentDto } from '../../api/cms';
import { fetchAiStatus, analyzeDocument } from '../../api/ai';
import type { DocumentAnalysisResult } from '../../types/documentIntelligence';
import { VaultIntelligenceReview } from './VaultIntelligenceReview';
import { VaultManualCropEditor } from './VaultManualCropEditor';
import { filePreviewForAi } from '../../lib/pdfConvert';
import { ensureAiStatus, isAnalysisEnabled } from '../../lib/aiStatus';
import type { UploadVaultDocumentInput } from '../../api/vault';
import { useUiSound } from '../../hooks/useUiSound';
import {
  delay,
  filesToPageDrafts,
  findDuplicateDocument,
  formatUploadBytes,
  deriveNameFromFileName,
  inferAutoTags,
  readBlobAsBase64WithProgress,
  rescanPageDraft,
  resolveUploadFile,
  revokePageDrafts,
  applyManualCropToPage,
  applyScanFilter,
  blobToFile,
  VAULT_UPLOAD_ACCEPT,
  type ScanFilter,
  type VaultPageDraft,
} from '../../lib/vaultUpload';

type FlowStep = 'pick' | 'pages' | 'review' | 'uploading' | 'success';

interface VaultUploadFlowProps {
  open: boolean;
  onClose: () => void;
  categories: VaultCategoryDto[];
  existingDocuments: VaultDocumentDto[];
  mode?: 'create' | 'replace';
  replaceDocument?: VaultDocumentDto;
  initialCategoryId?: string;
  initialSubcategory?: string;
  onSubmit: (input: UploadVaultDocumentInput) => Promise<VaultDocumentDto>;
  onAddCategoryItem?: (categoryId: string, item: string) => Promise<void>;
  onViewDocument?: (doc: VaultDocumentDto) => void;
}

export function VaultUploadFlow({
  open,
  onClose,
  categories,
  existingDocuments,
  mode = 'create',
  replaceDocument,
  initialCategoryId = 'identity',
  initialSubcategory,
  onSubmit,
  onAddCategoryItem,
  onViewDocument,
}: VaultUploadFlowProps) {
  const { play } = useUiSound();
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const [step, setStep] = useState<FlowStep>('pick');
  const [pages, setPages] = useState<VaultPageDraft[]>([]);
  const [activePageId, setActivePageId] = useState<string | null>(null);
  const [saveAsPdf, setSaveAsPdf] = useState(true);
  const [dragOver, setDragOver] = useState(false);

  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState(initialCategoryId);
  const [subcategory, setSubcategory] = useState(initialSubcategory ?? '');
  const [customSubcategory, setCustomSubcategory] = useState('');
  const [showCustomSubcategory, setShowCustomSubcategory] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagsInput, setTagsInput] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [aiStage, setAiStage] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<DocumentAnalysisResult | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [duplicate, setDuplicate] = useState<{ name: string; subcategory: string } | null>(null);
  const [duplicateAction, setDuplicateAction] = useState<'keep' | null>(null);

  const [uploadLabel, setUploadLabel] = useState('Uploading…');
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadBytes, setUploadBytes] = useState({ done: 0, total: 0 });
  const [savedDoc, setSavedDoc] = useState<VaultDocumentDto | null>(null);
  const [scanning, setScanning] = useState(false);
  const [manualCropPageId, setManualCropPageId] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status'],
    queryFn: fetchAiStatus,
    staleTime: 60_000,
    enabled: open,
    retry: 2,
  });

  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches;
  const category = categories.find((c) => c.id === categoryId) ?? categories[0];
  const subcategories = category?.items ?? [];
  const isEducation = categoryId === 'education';
  const activePage = pages.find((p) => p.id === activePageId) ?? pages[0] ?? null;
  const resolvedSubcategory = showCustomSubcategory ? customSubcategory.trim() : subcategory;

  const reset = useCallback(() => {
    setStep(mode === 'replace' ? 'pick' : 'pick');
    setPages((prev) => {
      revokePageDrafts(prev);
      return [];
    });
    setActivePageId(null);
    setSaveAsPdf(true);
    setName(replaceDocument?.name ?? '');
    setCategoryId(replaceDocument?.categoryId ?? initialCategoryId);
    setSubcategory(replaceDocument?.subcategory ?? initialSubcategory ?? categories.find((c) => c.id === initialCategoryId)?.items[0] ?? '');
    setShowCustomSubcategory(false);
    setCustomSubcategory('');
    setExpiryDate('');
    setDescription('');
    setTags([]);
    setTagsInput('');
    setError(null);
    setAiStage(null);
    setAiAnalysis(null);
    setDuplicate(null);
    setDuplicateAction(null);
    setUploadPercent(0);
    setSavedDoc(null);
    setScanning(false);
    setManualCropPageId(null);
  }, [categories, initialCategoryId, initialSubcategory, mode, replaceDocument]);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const ingestFiles = async (files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setError(null);
    setScanning(true);
    try {
      const drafts = await filesToPageDrafts(list);
      setPages((prev) => {
        const merged = [...prev, ...drafts];
        setActivePageId(merged[merged.length - 1]?.id ?? null);
        setSaveAsPdf(merged.length > 1 || merged.some((p) => p.blob.type === 'application/pdf'));
        return merged;
      });
      setStep('pages');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not read file.');
    } finally {
      setScanning(false);
    }
  };

  const recropActivePage = async () => {
    if (!activePage?.originalBlob) return;
    setScanning(true);
    setError(null);
    try {
      const next = await rescanPageDraft(activePage);
      setPages((prev) => prev.map((p) => (p.id === activePage.id ? next : p)));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not re-crop document.');
    } finally {
      setScanning(false);
    }
  };

  const manualCropPage = pages.find((p) => p.id === manualCropPageId) ?? null;

  const applyManualCrop = (croppedBlob: Blob) => {
    if (!manualCropPage) return;
    setPages((prev) => prev.map((p) => (
      p.id === manualCropPage.id ? applyManualCropToPage(p, croppedBlob) : p
    )));
    setManualCropPageId(null);
    setError(null);
  };

  function blobToPreviewFile(blob: Blob, fileName: string): File {
    return blobToFile(blob, fileName);
  }

  const applyAnalysis = (analysis: DocumentAnalysisResult) => {
    if (analysis.name.value) setName(String(analysis.name.value));
    if (analysis.description.value) setDescription(String(analysis.description.value));
    if (analysis.categoryId.value && categories.some((c) => c.id === analysis.categoryId.value)) {
      setCategoryId(analysis.categoryId.value);
      const target = categories.find((c) => c.id === analysis.categoryId.value);
      const items = target?.items ?? [];
      const sub = String(analysis.subcategory.value || analysis.documentType.value || '');
      if (sub && items.some((item) => item.toLowerCase() === sub.toLowerCase())) {
        setShowCustomSubcategory(false);
        setSubcategory(sub);
      } else if (sub) {
        setShowCustomSubcategory(true);
        setCustomSubcategory(sub);
      }
    }
    if (analysis.tags.value?.length) {
      setTags(analysis.tags.value);
      setTagsInput(analysis.tags.value.join(', '));
    }
    if (analysis.expiryDate.value) setExpiryDate(String(analysis.expiryDate.value));
  };

  const applyFallbackMetadata = (fileName: string, mimeType: string) => {
    const fallbackName = deriveNameFromFileName(fileName, mimeType);
    setName((current) => current || fallbackName);
    const sub = resolvedSubcategory || 'Document';
    setTags(inferAutoTags(categoryId, sub));
    setTagsInput(inferAutoTags(categoryId, sub).join(', '));
  };

  const runAiAnalysis = async (fileBlob: Blob, fileName: string, mimeType: string) => {
    setAiLoading(true);
    setAiStage('Analyzing document…');
    try {
      await delay(200);
      setAiStage('OCR & vision analysis…');
      const preview = mimeType.startsWith('image/') || mimeType === 'application/pdf'
        ? await filePreviewForAi(blobToPreviewFile(fileBlob, fileName))
        : undefined;
      await delay(200);
      setAiStage('Extracting metadata…');

      const status = await ensureAiStatus(queryClient, aiStatus);
      if (isAnalysisEnabled(status)) {
        if (status.billingMessage) {
          setError(status.billingMessage);
          applyFallbackMetadata(fileName, mimeType);
          return;
        }
        const analysis = await analyzeDocument({
          fileName,
          mimeType,
          previewImageBase64: preview?.base64,
          previewMimeType: preview?.mimeType,
        });
        setAiAnalysis(analysis);
        applyAnalysis(analysis);
      } else {
        applyFallbackMetadata(fileName, mimeType);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'AI analysis failed. Fill in details manually.');
      applyFallbackMetadata(fileName, mimeType);
    } finally {
      setAiStage(null);
      setAiLoading(false);
    }
  };

  const goToReview = async () => {
    if (pages.length === 0) {
      setError('Add at least one page.');
      return;
    }
    setError(null);
    setStep('review');

    try {
      const { blob, fileName, mimeType } = await resolveUploadFile(pages, saveAsPdf);
      if (!name.trim()) {
        await runAiAnalysis(blob, fileName, mimeType);
      } else {
        setTags((current) => current.length ? current : inferAutoTags(categoryId, resolvedSubcategory || subcategories[0] || ''));
      }
      const dup = findDuplicateDocument(existingDocuments, name || fileName, resolvedSubcategory);
      setDuplicate(dup);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not prepare preview.');
    }
  };

  const updateActiveFilter = async (filter: ScanFilter) => {
    if (!activePage) return;
    const next = await applyScanFilter(activePage.blob, filter, activePage.rotation);
    setPages((prev) => prev.map((p) => {
      if (p.id !== activePage.id) return p;
      URL.revokeObjectURL(p.previewUrl);
      return { ...p, blob: next, previewUrl: URL.createObjectURL(next), filter };
    }));
  };

  const rotateActive = async () => {
    if (!activePage) return;
    const rotation = (activePage.rotation + 90) % 360;
    const next = await applyScanFilter(activePage.blob, activePage.filter, rotation);
    setPages((prev) => prev.map((p) => {
      if (p.id !== activePage.id) return p;
      URL.revokeObjectURL(p.previewUrl);
      return { ...p, blob: next, previewUrl: URL.createObjectURL(next), rotation };
    }));
  };

  const removePage = (id: string) => {
    setPages((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      const next = prev.filter((p) => p.id !== id);
      if (activePageId === id) setActivePageId(next[0]?.id ?? null);
      if (next.length === 0) setStep('pick');
      return next;
    });
  };

  const handleAddCustomLevel = async () => {
    const item = customSubcategory.trim();
    if (!item) {
      setError('Enter a type name.');
      return;
    }
    if (onAddCategoryItem) {
      try {
        await onAddCategoryItem(categoryId, item);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Could not save type.');
        return;
      }
    }
    setSubcategory(item);
    setShowCustomSubcategory(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!name.trim() || !categoryId || !resolvedSubcategory) {
      setError('Fill in all required fields.');
      return;
    }
    if (duplicate && !duplicateAction) {
      setError('Choose how to handle the duplicate document.');
      return;
    }

    setStep('uploading');
    setUploadPercent(0);
    setError(null);
    play('uploadStart');

    try {
      const { blob, fileName, mimeType } = await resolveUploadFile(pages, saveAsPdf);
      setUploadBytes({ done: 0, total: blob.size });
      setUploadLabel('Encrypting & uploading…');

      const fileData = await readBlobAsBase64WithProgress(blob, (percent) => {
        setUploadPercent(Math.min(90, Math.round(percent * 0.85)));
        setUploadBytes({ done: Math.round((blob.size * percent) / 100), total: blob.size });
      });

      setUploadLabel('Saving to vault…');
      setUploadPercent(95);

      const tagList = tagsInput.trim()
        ? tagsInput.split(',').map((t) => t.trim()).filter(Boolean)
        : tags;

      const doc = await onSubmit({
        name: name.trim(),
        categoryId,
        subcategory: resolvedSubcategory,
        expiryDate: expiryDate || undefined,
        fileName: `${name.trim().replace(/[^\w\s.-]/g, '').replace(/\s+/g, '-')}.${mimeType === 'application/pdf' ? 'pdf' : 'jpg'}`,
        mimeType,
        fileData,
        tags: tagList,
        description: description.trim() || undefined,
        detectedDocType: aiAnalysis?.documentType.value || undefined,
        aiMetadata: aiAnalysis || undefined,
        aiAnalysisStatus: aiAnalysis ? 'complete' : 'skipped',
      });

      setUploadPercent(100);
      setSavedDoc(doc);
      setStep('success');
      play('uploadComplete');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload failed.');
      play('error');
      setStep('review');
    }
  };

  const previewMeta = useMemo(() => {
    if (pages.length === 0) return null;
    const totalBytes = pages.reduce((sum, p) => sum + p.blob.size, 0);
    return {
      pages: pages.length,
      size: formatUploadBytes(totalBytes),
    };
  }, [pages]);

  if (!open) return null;

  return (
    <div className="wfx-vault-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-vault-modal glass-surface wfx-upload-flow"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vault-upload-flow-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-vault-modal-head">
          <div>
            <h2 id="vault-upload-flow-title">
              {step === 'success' ? 'Upload complete' : mode === 'replace' ? 'Replace document' : 'Quick upload'}
            </h2>
            {step !== 'success' && (
              <p className="wfx-muted wfx-upload-subtitle">
                {step === 'pick' && 'Capture or select — review — save in under 15 seconds'}
                {step === 'pages' && `${pages.length} page${pages.length === 1 ? '' : 's'} ready`}
                {step === 'review' && 'Review and save'}
                {step === 'uploading' && uploadLabel}
              </p>
            )}
          </div>
          <button type="button" className="ghost-icon-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>

        {error && step !== 'uploading' && <div className="auth-error" role="alert">{error}</div>}

        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          onChange={(e) => {
            if (e.target.files) void ingestFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          multiple
          className="sr-only"
          onChange={(e) => {
            if (e.target.files) void ingestFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <input
          ref={fileRef}
          type="file"
          accept={VAULT_UPLOAD_ACCEPT}
          multiple
          className="sr-only"
          onChange={(e) => {
            if (e.target.files) void ingestFiles(e.target.files);
            e.target.value = '';
          }}
        />

        {step === 'pick' && (
          <div className="wfx-upload-pick">
            <div className="wfx-upload-methods">
              <button
                type="button"
                className={`wfx-upload-method glass-surface${isMobile ? ' is-primary' : ''}`}
                onClick={() => cameraRef.current?.click()}
              >
                <Camera size={28} aria-hidden="true" />
                <strong>Capture with Camera</strong>
                <span>Smart crop · auto-enhance · multi-page</span>
              </button>
              <button
                type="button"
                className="wfx-upload-method glass-surface"
                onClick={() => galleryRef.current?.click()}
              >
                <ImageIcon size={28} aria-hidden="true" />
                <strong>Choose from Gallery</strong>
                <span>Single or multiple images</span>
              </button>
              <button
                type="button"
                className="wfx-upload-method glass-surface"
                onClick={() => fileRef.current?.click()}
              >
                <FolderOpen size={28} aria-hidden="true" />
                <strong>Upload Files</strong>
                <span>PDF, JPG, PNG, WEBP, HEIC</span>
              </button>
            </div>

            <div
              ref={dropRef}
              className={`wfx-vault-file-drop wfx-upload-dropzone${dragOver ? ' is-dragover' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (e.dataTransfer.files.length) void ingestFiles(e.dataTransfer.files);
              }}
            >
              <Upload size={22} aria-hidden="true" />
              <strong>Drag & drop files here</strong>
              <span className="wfx-muted">Desktop · multiple files supported</span>
            </div>

            <p className="wfx-upload-trust">
              <Lock size={14} aria-hidden="true" /> Encrypted before upload
            </p>
          </div>
        )}

        {step === 'pages' && (
          <div className="wfx-upload-pages">
            {scanning && (
              <div className="wfx-upload-scanning glass-surface">
                <ScanLine size={18} aria-hidden="true" />
                <span>Detecting document edges and cropping…</span>
              </div>
            )}

            {pages.some((p) => p.autoCropped) && !scanning && (
              <p className="wfx-upload-scan-note">
                <ScanLine size={14} aria-hidden="true" />
                {pages.filter((p) => p.autoCropped).length} page{pages.filter((p) => p.autoCropped).length === 1 ? '' : 's'} auto-cropped to document edges
              </p>
            )}

            {pages.some((p) => p.originalBlob && p.scanMethod === 'none') && !scanning && (
              <p className="wfx-upload-scan-note wfx-upload-scan-note--warn">
                Could not detect document edges on {pages.filter((p) => p.originalBlob && p.scanMethod === 'none').length} page{pages.filter((p) => p.originalBlob && p.scanMethod === 'none').length === 1 ? '' : 's'}. Use Manual crop or Re-crop.
              </p>
            )}

            <div className="wfx-upload-page-preview glass-surface">
              {activePage ? (
                activePage.blob.type === 'application/pdf' ? (
                  activePage.previewImageUrl ? (
                    <img src={activePage.previewImageUrl} alt="PDF preview" />
                  ) : (
                    <div className="wfx-upload-pdf-badge">PDF · {formatUploadBytes(activePage.blob.size)}</div>
                  )
                ) : (
                  <img src={activePage.previewUrl} alt="Page preview" />
                )
              ) : (
                <p className="wfx-muted">No pages yet</p>
              )}
            </div>

            <div className="wfx-upload-page-strip">
              {pages.map((page, index) => (
                <div
                  key={page.id}
                  className={`wfx-upload-page-thumb${page.id === activePage?.id ? ' is-active' : ''}`}
                >
                  <button type="button" className="wfx-upload-page-thumb-btn" onClick={() => setActivePageId(page.id)}>
                    {page.blob.type === 'application/pdf' ? (
                      page.previewImageUrl ? (
                        <img src={page.previewImageUrl} alt={`Page ${index + 1}`} />
                      ) : (
                        <span>PDF</span>
                      )
                    ) : (
                      <img src={page.previewUrl} alt={`Page ${index + 1}`} />
                    )}
                    <span>{index + 1}</span>
                  </button>
                  <button
                    type="button"
                    className="wfx-upload-page-remove"
                    aria-label="Remove page"
                    onClick={() => removePage(page.id)}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <button type="button" className="wfx-upload-page-add" onClick={() => cameraRef.current?.click()}>
                <Camera size={18} /> Add page
              </button>
            </div>

            {activePage && (activePage.originalBlob || activePage.blob.type.startsWith('image/')) && (
              <div className="wfx-upload-enhance">
                <span>Enhance</span>
                <div className="wfx-upload-enhance-actions">
                  {(['original', 'enhance', 'bw'] as ScanFilter[]).map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      className={`chip-pill${activePage.filter === filter ? ' is-active' : ''}`}
                      onClick={() => updateActiveFilter(filter)}
                    >
                      {filter === 'original' ? 'Original' : filter === 'enhance' ? 'Enhanced' : 'B&W'}
                    </button>
                  ))}
                  <button type="button" className="chip-pill" onClick={rotateActive}>
                    <RotateCw size={14} /> Rotate
                  </button>
                  <button type="button" className="chip-pill" onClick={() => void recropActivePage()} disabled={scanning}>
                    <ScanLine size={14} /> Re-crop
                  </button>
                  <button
                    type="button"
                    className="chip-pill"
                    onClick={() => activePage?.originalBlob && setManualCropPageId(activePage.id)}
                    disabled={scanning || !activePage?.originalBlob}
                  >
                    <Crop size={14} /> Manual crop
                  </button>
                </div>
              </div>
            )}

            {pages.length > 0 && pages.every((p) => p.blob.type.startsWith('image/')) && (
              <label className="wfx-toggle-row">
                <span>Combine {pages.length} pages as PDF</span>
                <input
                  type="checkbox"
                  checked={saveAsPdf}
                  onChange={(e) => setSaveAsPdf(e.target.checked)}
                />
              </label>
            )}

            <div className="wfx-vault-modal-actions">
              <button type="button" className="secondary-action" onClick={() => setStep('pick')}>Back</button>
              <button type="button" className="primary-action" onClick={() => void goToReview()}>
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 'review' && (
          <form
            className="wfx-vault-upload-form"
            onSubmit={(e) => { e.preventDefault(); void handleSave(); }}
          >
            {previewMeta && (
              <div className="wfx-upload-preview-meta glass-surface">
                {(activePage?.previewImageUrl || activePage?.blob.type.startsWith('image/')) && (
                  <img
                    src={activePage?.previewImageUrl ?? activePage?.previewUrl}
                    alt=""
                    className="wfx-upload-preview-thumb"
                  />
                )}
                <div>
                  <strong>{name || 'Untitled document'}</strong>
                  <p className="wfx-muted">
                    {category?.label} · {previewMeta.pages} page{previewMeta.pages === 1 ? '' : 's'} · {previewMeta.size}
                  </p>
                </div>
              </div>
            )}

            {duplicate && !duplicateAction && (
              <div className="wfx-upload-duplicate glass-surface" role="alert">
                <p>Looks like <strong>{duplicate.name}</strong> already exists.</p>
                <div className="wfx-upload-duplicate-actions">
                  <button type="button" className="secondary-action compact" onClick={() => setDuplicateAction('keep')}>
                    Keep both
                  </button>
                  <button type="button" className="text-action" onClick={() => setStep('pages')}>
                    Retake
                  </button>
                  <button type="button" className="text-action" onClick={onClose}>Cancel</button>
                </div>
              </div>
            )}

            <VaultIntelligenceReview
              analysis={aiAnalysis}
              loading={aiLoading}
              stage={aiStage}
              name={name}
              description={description}
              categoryId={categoryId}
              subcategory={resolvedSubcategory}
              tagsInput={tagsInput}
              expiryDate={expiryDate}
              onNameChange={setName}
              onDescriptionChange={setDescription}
              onCategoryChange={(value) => {
                setCategoryId(value);
                setShowCustomSubcategory(false);
                const items = categories.find((c) => c.id === value)?.items ?? [];
                if (items[0]) setSubcategory(items[0]);
              }}
              onSubcategoryChange={(value) => {
                if (showCustomSubcategory) setCustomSubcategory(value);
                else setSubcategory(value);
              }}
              onTagsChange={setTagsInput}
              onExpiryChange={setExpiryDate}
              categories={categories}
            />

            {showCustomSubcategory && onAddCategoryItem && (
              <div className="wfx-vault-custom-level">
                <button type="button" className="text-action" onClick={() => void handleAddCustomLevel()}>
                  Save {isEducation ? 'academic level' : 'type'} for future uploads
                </button>
                <button
                  type="button"
                  className="text-action"
                  onClick={() => {
                    setShowCustomSubcategory(false);
                    setSubcategory(subcategories[0] ?? '');
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            <div className="wfx-vault-modal-actions">
              <button type="button" className="secondary-action" onClick={() => setStep('pages')}>Edit scan</button>
              <button type="submit" className="primary-action" disabled={aiLoading}>
                {mode === 'replace' ? 'Replace file' : 'Save to vault'}
              </button>
            </div>
          </form>
        )}

        {step === 'uploading' && (
          <div className="wfx-upload-progress-panel">
            <p className="wfx-upload-progress-label">{uploadLabel}</p>
            <div className="wfx-download-progress-track">
              <span style={{ width: `${uploadPercent}%` }} />
            </div>
            <p className="wfx-muted wfx-upload-bytes">
              {formatUploadBytes(uploadBytes.done)} / {formatUploadBytes(uploadBytes.total)}
              {' · '}{uploadPercent}%
            </p>
            <p className="wfx-upload-trust"><Lock size={14} /> Encrypted before upload</p>
          </div>
        )}

        {manualCropPage?.originalBlob && (
          <VaultManualCropEditor
            sourceBlob={manualCropPage.originalBlob}
            onApply={applyManualCrop}
            onCancel={() => setManualCropPageId(null)}
          />
        )}

        {step === 'success' && savedDoc && (
          <div className="wfx-upload-success">
            <CheckCircle2 size={48} className="wfx-upload-success-icon" aria-hidden="true" />
            <h3>Uploaded successfully</h3>
            <p><strong>{savedDoc.name}</strong></p>
            <p className="wfx-muted">Stored in {categories.find((c) => c.id === savedDoc.categoryId)?.label ?? 'Vault'}</p>
            <div className="wfx-vault-modal-actions">
              <button type="button" className="secondary-action" onClick={onClose}>Done</button>
              {onViewDocument && (
                <button type="button" className="primary-action" onClick={() => { onViewDocument(savedDoc); onClose(); }}>
                  View document
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
